'use client';

import { useProject } from '@/contexts/ProjectContext';
import { useProjectBoards, useProjectData } from '@/hooks/useProjectData';
import { useTaskKanbanColumnUpdate, useTaskOrderUpdate, useTaskSprintOrderUpdate, useTaskSprintUpdate, useTaskStatusUpdate } from '@/hooks/useTaskStageUpdate';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import KanbanColumn from './KanbanColumn';
import SprintHeaderBar from './SprintHeaderBar';
import TaskModal from './TaskModal';

interface Task {
  id: number;
  title: string;
  easy_id: number | null;
  project: number;
  sprint: number | null;
  kanban_column: number | null;
  type: number; // 0 = Feature, 1 = Bug
  status: number; // 0 = Not Started, 1 = In Progress, 2 = Delivered, 3 = Done
  assigned_to: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  sprint_order?: number;
  order?: number;
  labels?: Array<{
    id: number;
    name: string;
    color: number;
    color_display: string;
  }>;
  child_count?: number;
  release?: {
    id: number;
    name: string;
  } | null;
}

interface Board {
  id: number;
  name: string;
  kanban_column_type: number | null;
  order: number;
  project: number;
}

const MAX_PROJECT_BOARDS = 7;

// Cycled across project-view board columns by index
const BOARD_COLORS = ['blue', 'green', 'orange', 'pink', 'purple', 'teal', 'red'];

export default function KanbanBoard() {
  const queryClient = useQueryClient();
  const { selectedProject, selectedSprint } = useProject();
  const { data: tasksData = [], isLoading: loading } = useProjectData(
    selectedProject?.id ?? null,
    selectedSprint?.id
  );
  const { data: boardsData = [] } = useProjectBoards(
    selectedProject?.type === 0 && !selectedSprint ? selectedProject.id : null
  );
  
  const updateStatusMutation = useTaskStatusUpdate();
  const updateKanbanColumnMutation = useTaskKanbanColumnUpdate();
  const updateSprintMutation = useTaskSprintUpdate();
  const updateSprintOrderMutation = useTaskSprintOrderUpdate();
  const updateOrderMutation = useTaskOrderUpdate();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | undefined>();

  // Use boards data directly
  const boards = boardsData;
  const tasks = tasksData as Task[];

  const handleTaskCreate = async (title: string, kanbanColumn: number) => {
    if (!selectedProject) return;

    try {
      const requestBody: Record<string, unknown> = {
        title,
        project: selectedProject.id,
        status: 0, // Not Started
        priority: 1, // Medium
      };

      if (selectedSprint) {
        // In kanban board (sprint view), set sprint and status based on column
        requestBody.sprint = selectedSprint.id;
        requestBody.status = kanbanColumn; // 0 = Not Started, 1 = In Progress, 2 = Testing, 3 = Done
      } else {
        // In project view, set kanban_column
        requestBody.kanban_column = kanbanColumn;
      }

      const response = await apiRequest(API_ENDPOINTS.TASKS.LIST, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        queryClient.invalidateQueries({ 
          queryKey: ['project', selectedProject.id, 'tasks', selectedSprint?.id] 
        });
      }
    } catch (error) {
      // Task creation failed silently
    }
  };

  const handleTaskClick = (taskId: number) => {
    // Find which column this task is in
    const task = tasks.find(t => t.id === taskId);
    setEditingTaskId(taskId);
    setIsModalOpen(true);
  };

  const handleTaskDragStart = (e: React.DragEvent, taskId: number) => {
    e.dataTransfer.setData('taskId', taskId.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDrop = async (e: React.DragEvent, taskId: number, newStatus: number) => {
    e.preventDefault();
    updateStatusMutation.mutate({
      taskId,
      status: newStatus,
    });
  };

  const handleTaskDropToColumn = async (e: React.DragEvent, taskId: number, newKanbanColumn: number) => {
    e.preventDefault();
    updateKanbanColumnMutation.mutate({
      taskId,
      kanbanColumn: newKanbanColumn,
    });
  };

  const handleTaskReorder = async (draggedTaskId: number, targetTaskId: number, status: number) => {
    // Get all tasks in the same status column, sorted by current sprint_order
    const columnTasks = tasks
      .filter((task: Task) => task.status === status)
      .sort((a, b) => {
        const aOrder = a.sprint_order ?? a.order ?? 0;
        const bOrder = b.sprint_order ?? b.order ?? 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.id - b.id;
      });
    
    // Find indices
    const draggedIndex = columnTasks.findIndex((t: Task) => t.id === draggedTaskId);
    const targetIndex = columnTasks.findIndex((t: Task) => t.id === targetTaskId);
    
    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return;
    
    // Create new array with reordered tasks
    const reorderedTasks = [...columnTasks];
    const [draggedTask] = reorderedTasks.splice(draggedIndex, 1);
    reorderedTasks.splice(targetIndex, 0, draggedTask);
    
    // Update sprint_order for all tasks in the column
    reorderedTasks.forEach((task: Task, index: number) => {
      const currentOrder = task.sprint_order ?? task.order ?? 0;
      if (currentOrder !== index) {
        updateSprintOrderMutation.mutate({
          taskId: task.id,
          sprintOrder: index,
        });
      }
    });
  };

  // Project-view boards sorted by display order. Each board occupies one "slot"
  // (kanban_column_type 0-6); a task belongs to the board whose slot equals its
  // kanban_column. Tasks with no/orphaned column fall into the first board.
  const sortedBoards = useMemo(
    () => [...(boards as Board[])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [boards]
  );

  const sortByOrder = (taskList: Task[]) =>
    [...taskList].sort((a, b) => {
      const aOrder = a.order ?? 0;
      const bOrder = b.order ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id - b.id;
    });

  // Tasks belonging to a given board slot (strict per-column). The first board also
  // absorbs tasks with a null or orphaned column so nothing is ever hidden.
  const getProjectColumnTasks = (slot: number | null): Task[] => {
    const validSlots = new Set(sortedBoards.map((b) => b.kanban_column_type));
    const isFirst = sortedBoards.length > 0 && sortedBoards[0].kanban_column_type === slot;
    return tasks.filter((task: Task) => {
      if (task.sprint) return false;
      if (task.kanban_column === slot) return true;
      if (isFirst && (task.kanban_column === null || task.kanban_column === undefined || !validSlots.has(task.kanban_column))) {
        return true;
      }
      return false;
    });
  };

  const handleTaskColumnReorder = async (draggedTaskId: number, targetTaskId: number, kanbanColumn: number) => {
    // Get all tasks in the same kanban column, sorted to match what's rendered
    const columnTasks = sortByOrder(getProjectColumnTasks(kanbanColumn));

    const draggedIndex = columnTasks.findIndex((t: Task) => t.id === draggedTaskId);
    const targetIndex = columnTasks.findIndex((t: Task) => t.id === targetTaskId);

    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return;

    // Create new array with reordered tasks
    const reorderedTasks = [...columnTasks];
    const [draggedTask] = reorderedTasks.splice(draggedIndex, 1);
    reorderedTasks.splice(targetIndex, 0, draggedTask);

    // Persist the new order for any task whose position changed
    reorderedTasks.forEach((task: Task, index: number) => {
      if ((task.order ?? 0) !== index) {
        updateOrderMutation.mutate({
          taskId: task.id,
          order: index,
        });
      }
    });
  };

  const invalidateBoards = () => {
    queryClient.invalidateQueries({ queryKey: ['project', selectedProject?.id, 'boards'] });
  };

  const handleBoardCreate = async () => {
    if (!selectedProject || sortedBoards.length >= MAX_PROJECT_BOARDS) return;

    // Pick the smallest unused slot (0-6) and place the board at the end
    const usedSlots = new Set(sortedBoards.map((b) => b.kanban_column_type));
    let slot = 0;
    while (slot < MAX_PROJECT_BOARDS && usedSlots.has(slot)) slot++;
    const maxOrder = sortedBoards.reduce((m, b) => Math.max(m, b.order ?? 0), 0);

    try {
      const response = await apiRequest(API_ENDPOINTS.BOARDS, {
        method: 'POST',
        body: JSON.stringify({
          name: 'New Board',
          project: selectedProject.id,
          type: 0,
          color: 0,
          order: maxOrder + 1,
          kanban_column_type: slot,
        }),
      });
      if (response.ok) invalidateBoards();
    } catch (error) {
      // Board creation failed silently
    }
  };

  const handleBoardDelete = async (boardId: number) => {
    if (!selectedProject) return;
    if (!window.confirm('Delete this board? Any tasks in it will move to the first board.')) return;

    try {
      const response = await apiRequest(`${API_ENDPOINTS.BOARDS}${boardId}/`, {
        method: 'DELETE',
      });
      if (response.ok) {
        invalidateBoards();
        // Tasks were rehomed server-side; refresh them too
        queryClient.invalidateQueries({
          queryKey: ['project', selectedProject.id, 'tasks', selectedSprint?.id],
        });
      }
    } catch (error) {
      // Board deletion failed silently
    }
  };

  const handleBoardMove = async (boardId: number, direction: -1 | 1) => {
    if (!selectedProject) return;
    const index = sortedBoards.findIndex((b) => b.id === boardId);
    const swapIndex = index + direction;
    if (index === -1 || swapIndex < 0 || swapIndex >= sortedBoards.length) return;

    const a = sortedBoards[index];
    const b = sortedBoards[swapIndex];
    // Swap display order; if orders happen to be equal, nudge to guarantee a change
    const aOrder = a.order ?? 0;
    const bOrder = b.order ?? 0;
    const newAOrder = aOrder === bOrder ? aOrder + direction : bOrder;

    try {
      await Promise.all([
        apiRequest(`${API_ENDPOINTS.BOARDS}${a.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({ order: newAOrder }),
        }),
        apiRequest(`${API_ENDPOINTS.BOARDS}${b.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({ order: aOrder }),
        }),
      ]);
      invalidateBoards();
    } catch (error) {
      // Board reorder failed silently
    }
  };

  const handleTaskSaved = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['project', selectedProject?.id, 'tasks', selectedSprint?.id] 
    });
  };

  const handleRemoveFromSprint = async (taskId: number) => {
    updateSprintMutation.mutate({
      taskId,
      sprintId: null,
    });
  };

  const handleBoardNameUpdate = async (boardId: number, newName: string) => {
    try {
      const response = await apiRequest(`${API_ENDPOINTS.BOARDS}${boardId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ name: newName }),
      });

      if (response.ok) {
        // Invalidate boards query to refresh
        queryClient.invalidateQueries({ 
          queryKey: ['project', selectedProject?.id, 'boards'] 
        });
      } else {
        // Invalidate to revert to server state
        queryClient.invalidateQueries({ 
          queryKey: ['project', selectedProject?.id, 'boards'] 
        });
      }
    } catch (error) {
      // Invalidate to revert to server state
      queryClient.invalidateQueries({ 
        queryKey: ['project', selectedProject?.id, 'boards'] 
      });
    }
  };

  // Sprint-view groups (by status). Project view is handled by projectColumns below.
  const taskGroups = useMemo(() => {
    if (!selectedSprint) {
      return { notStarted: [], inProgress: [], testing: [], done: [] };
    }
    // Kanban board (sprint view): Filter by status
    // Status: 0 = Not Started, 1 = In Progress, 2 = Delivered (Testing), 3 = Done
    const mapTask = (task: Task, status: number) => ({
      id: task.id,
      title: task.title,
      taskId: task.easy_id ? `#${selectedProject?.initials || 'PRJ'}-${task.easy_id}` : `#TASK-${task.id}`,
      assignedTo: task.assigned_to,
      status,
    });

    // Sort tasks by sprint_order, then by order, then by id
    const sortTasks = (taskList: Task[]) => {
      return [...taskList].sort((a, b) => {
        if (a.sprint_order !== undefined && b.sprint_order !== undefined) {
          if (a.sprint_order !== b.sprint_order) {
            return a.sprint_order - b.sprint_order;
          }
        }
        if (a.order !== undefined && b.order !== undefined) {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
        }
        return a.id - b.id;
      });
    };

    return {
      notStarted: sortTasks(tasks.filter((task: Task) => task.status === 0)).map((task: Task) => mapTask(task, 0)),
      inProgress: sortTasks(tasks.filter((task: Task) => task.status === 1)).map((task: Task) => mapTask(task, 1)),
      testing: sortTasks(tasks.filter((task: Task) => task.status === 2)).map((task: Task) => mapTask(task, 2)),
      done: sortTasks(tasks.filter((task: Task) => task.status === 3)).map((task: Task) => mapTask(task, 3)),
    };
  }, [tasks, selectedSprint, selectedProject]);

  // Project view: one column per board, tasks grouped strictly by board slot.
  const projectColumns = useMemo(() => {
    if (selectedSprint) return [];
    return sortedBoards.map((board) => ({
      board,
      slot: board.kanban_column_type,
      tasks: sortByOrder(getProjectColumnTasks(board.kanban_column_type)).map((task) => ({
        id: task.id,
        title: task.title,
        taskId: task.easy_id ? `#${selectedProject?.initials || 'PRJ'}-${task.easy_id}` : `#TASK-${task.id}`,
        labels: task.labels,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, sortedBoards, selectedSprint, selectedProject]);

  // Destructure sprint-view groups for easier access
  const {
    notStarted: notStartedTasks = [],
    inProgress: inProgressTasks = [],
    testing: testingTasks = [],
    done: doneTasks = [],
  } = taskGroups as unknown as Record<string, { id?: number; title: string; taskId: string; assignedTo?: Task['assigned_to']; status?: number }[]>;

  if (!selectedProject) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Select a project to view tasks</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {selectedSprint && (
        <SprintHeaderBar sprint={selectedSprint} tasks={tasks} />
      )}
      <div 
        className="flex-1 p-6 overflow-x-auto overflow-y-auto min-h-0 show-scrollbar"
        style={{ 
          height: 0,
          maxHeight: selectedSprint ? 'calc(100vh - 200px)' : '100%'
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400 text-sm">Loading tasks...</div>
          </div>
        ) : selectedSprint ? (
          // Kanban board (sprint view): Table-based layout matching old design
          <div style={{ width: '100%' }}>
          <table className="table-condensed" style={{
            textAlign: 'left',
            marginTop: '21px',
            fontSize: '11px',
            width: '99%',
            marginBottom: '18px',
            padding: '0px',
            backgroundColor: 'transparent',
          }}>
            <thead>
              <tr style={{
                color: '#FFFFFF',
                padding: '0px',
                fontSize: '11pt',
              }}>
                <th style={{
                  textAlign: 'center',
                  width: '230px',
                  opacity: 0.5,
                  fontSize: '11pt',
                  color: '#FFFFFF',
                  padding: '0px',
                }}>NOT STARTED</th>
                <th style={{
                  textAlign: 'center',
                  width: '230px',
                  opacity: 0.5,
                  fontSize: '11pt',
                  color: '#FFFFFF',
                  padding: '0px',
                }}>IN PROGRESS</th>
                <th style={{
                  textAlign: 'center',
                  width: '230px',
                  opacity: 0.5,
                  fontSize: '11pt',
                  color: '#FFFFFF',
                  padding: '0px',
                }}>TESTING</th>
                <th style={{
                  textAlign: 'center',
                  width: '230px',
                  opacity: 0.5,
                  fontSize: '11pt',
                  color: '#FFFFFF',
                  padding: '0px',
                }}>DONE</th>
              </tr>
            </thead>
            <tbody style={{
              padding: '0px',
            }}>
              <tr style={{
                padding: '0px',
                verticalAlign: 'top',
                borderBottom: '1px groove #878787',
              }}>
                <td className="sprint-task-content" style={{
                  padding: '0px',
                  paddingTop: '5px',
                  paddingBottom: '12px',
                  borderRight: '1px groove #808080',
                  verticalAlign: 'top',
                }}>
                  <KanbanColumn 
                    title="Not Started"
                    color="orange" 
                    tasks={notStartedTasks}
                    kanbanColumn={0}
                    isSprintView={true}
                    isEditable={false}
                    onTaskClick={handleTaskClick}
                    onTaskCreate={handleTaskCreate}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDrop={handleTaskDrop}
                    onTaskReorder={handleTaskReorder}
                    onRemoveFromSprint={handleRemoveFromSprint}
                  />
                </td>
                <td className="sprint-task-content" style={{
                  padding: '0px',
                  paddingTop: '5px',
                  paddingBottom: '12px',
                  borderRight: '1px groove #808080',
                  verticalAlign: 'top',
                }}>
                  <KanbanColumn 
                    title="In Progress"
                    color="green" 
                    tasks={inProgressTasks}
                    kanbanColumn={1}
                    isSprintView={true}
                    isEditable={false}
                    onTaskClick={handleTaskClick}
                    onTaskCreate={handleTaskCreate}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDrop={handleTaskDrop}
                    onTaskReorder={handleTaskReorder}
                    onRemoveFromSprint={handleRemoveFromSprint}
                  />
                </td>
                <td className="sprint-task-content" style={{
                  padding: '0px',
                  paddingTop: '5px',
                  paddingBottom: '12px',
                  borderRight: '1px groove #808080',
                  verticalAlign: 'top',
                }}>
                  <KanbanColumn 
                    title="Testing"
                    color="brown" 
                    tasks={testingTasks}
                    kanbanColumn={2}
                    isSprintView={true}
                    isEditable={false}
                    onTaskClick={handleTaskClick}
                    onTaskCreate={handleTaskCreate}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDrop={handleTaskDrop}
                    onTaskReorder={handleTaskReorder}
                    onRemoveFromSprint={handleRemoveFromSprint}
                  />
                </td>
                <td className="sprint-task-content" style={{
                  padding: '0px',
                  paddingTop: '5px',
                  paddingBottom: '12px',
                  borderRight: '0px',
                  verticalAlign: 'top',
                }}>
                  <KanbanColumn 
                    title="Done"
                    color="gray" 
                    tasks={doneTasks}
                    kanbanColumn={3}
                    isSprintView={true}
                    isEditable={false}
                    onTaskClick={handleTaskClick}
                    onTaskCreate={handleTaskCreate}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDrop={handleTaskDrop}
                    onTaskReorder={handleTaskReorder}
                    onRemoveFromSprint={handleRemoveFromSprint}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        ) : (
          // Project view: one column per board (dynamic, up to MAX_PROJECT_BOARDS)
          <div className="flex gap-4 items-stretch min-h-full min-w-max">
            {projectColumns.map((col, index) => (
              <KanbanColumn
                key={col.board.id}
                title={col.board.name}
                color={BOARD_COLORS[index % BOARD_COLORS.length]}
                tasks={col.tasks}
                kanbanColumn={col.slot ?? 0}
                board={col.board}
                isEditable={selectedProject.type === 0}
                onTaskClick={handleTaskClick}
                onTaskCreate={handleTaskCreate}
                onBoardNameUpdate={handleBoardNameUpdate}
                onTaskDragStart={handleTaskDragStart}
                onTaskDropToColumn={handleTaskDropToColumn}
                onTaskReorder={handleTaskColumnReorder}
                onBoardDelete={selectedProject.type === 0 ? handleBoardDelete : undefined}
                onBoardMove={selectedProject.type === 0 ? handleBoardMove : undefined}
                canMoveLeft={index > 0}
                canMoveRight={index < projectColumns.length - 1}
              />
            ))}
            {selectedProject.type === 0 && sortedBoards.length < MAX_PROJECT_BOARDS && (
              <div className="w-56 flex-shrink-0">
                <button
                  onClick={handleBoardCreate}
                  className="w-full border border-dashed border-[#2d1b3d] hover:border-blue-500 text-gray-400 hover:text-white rounded-lg px-3 py-2 text-sm transition-colors"
                >
                  + Add Board
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTaskId(undefined);
        }}
        mode={editingTaskId ? 'edit' : 'create'}
        taskId={editingTaskId}
        kanbanColumn={editingTaskId ? tasks.find(t => t.id === editingTaskId)?.kanban_column ?? undefined : undefined}
        onTaskSaved={handleTaskSaved}
      />
    </div>
  );
}

